import React, { useEffect, useMemo, useState } from "react";
import { oracleApi } from "@/api/oracleClient";
import KnowledgeGraphPanel from "@/components/KnowledgeGraphPanel";

const SECTIONS = [
  { id: "overview", title: "1. Platform Overview" },
  { id: "architecture", title: "2. Mode Architecture" },
  { id: "livedata", title: "3. Live Data Status" },
  { id: "mongo", title: "4. MongoDB Persistence" },
  { id: "oanda", title: "5. OANDA Integration" },
  { id: "compliance", title: "6. Risk & Compliance Layer" },
  { id: "export", title: "7. Export Center" },
  { id: "audit", title: "8. Audit Readiness" },
  { id: "knowledge", title: "9. Knowledge Graph" },
  { id: "alerts", title: "10. Watchlist Alerts" },
  { id: "auth", title: "11. Auth & RBAC" },
  { id: "roadmap", title: "12. Roadmap" },
  { id: "legal", title: "13. Legal Disclaimers" },
];

export default function ReportCenter({
  userId,
  currentSessionId,
  activeMode,
  metadata,
  watchlist,
  onClearSession,
  onExportPdf,
  onRefreshFeed,
  onAddToWatchlist,
  liveStatus,
  adminStatus,
  exportLoading,
}) {
  const [refreshing, setRefreshing] = useState(false);

  const noSession = !currentSessionId;
  const oandaConnected = !!liveStatus?.oanda?.configured;
  const mongoOk = adminStatus?.mongo === "connected";
  const llmOk = adminStatus?.anthropic === "configured";

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefreshFeed?.();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="report-center" data-testid="oracle-report-center">
      <header className="report-center__head">
        <div>
          <div className="report-center__kicker">CareLogix™ ORACLE</div>
          <h2 className="report-center__title">Report Center™</h2>
          <p className="report-center__sub">
            Enterprise operational view: data freshness, persistence, compliance, audit, exports.
          </p>
        </div>
        <div className="report-center__actions">
          <button
            type="button"
            className="oracle-btn oracle-btn--gold"
            onClick={onExportPdf}
            disabled={noSession || exportLoading}
            data-testid="report-center-export"
            title={noSession ? "Start a session to enable PDF export" : "Download branded dossier"}
          >
            {exportLoading ? "Generating…" : "⬇ Download Current Dossier PDF"}
          </button>
          <button
            type="button"
            className="oracle-btn oracle-btn--ghost"
            onClick={onClearSession}
            disabled={noSession}
            data-testid="report-center-clear"
          >
            Clear Current Session
          </button>
          <button
            type="button"
            className="oracle-btn oracle-btn--ghost"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="report-center-refresh"
          >
            {refreshing ? "Refreshing…" : "↻ Refresh Live Feed Status"}
          </button>
          <button
            type="button"
            className="oracle-btn oracle-btn--ghost"
            onClick={onAddToWatchlist}
            disabled={noSession}
            data-testid="report-center-watch"
          >
            ＋ Add Current Asset to Watchlist
          </button>
        </div>
      </header>

      {noSession && (
        <div className="report-center__empty">
          Start an ORACLE research session to generate a branded dossier.
        </div>
      )}

      <nav className="report-center__nav" aria-label="Report sections">
        {SECTIONS.map((s) => (
          <a key={s.id} href={`#rc-${s.id}`} className="rc-nav-link">
            {s.title}
          </a>
        ))}
      </nav>

      <section id="rc-overview" className="rc-section">
        <h3>1. Platform Overview</h3>
        <p>
          CareLogix™ ORACLE is a research and decision-support engine spanning 7 intelligence modes —
          Stock Picks, Crypto Plays, OANDA FX, Sports Bets, Options Flow, Swing Trades, and Catalyst Plays.
          The platform turns every assistant response into an auditable intelligence object via the
          ORACLE Command Layer™, supports live OANDA v20 FX data via secure server-side proxy, persists
          sessions in MongoDB, and exports branded PDF Intelligence Dossiers.
        </p>
      </section>

      <section id="rc-architecture" className="rc-section">
        <h3>2. Mode Architecture</h3>
        <ul>
          <li>7 modes, each with a dedicated system prompt + enriched mode intelligence.</li>
          <li>Every reply also produces an ORACLE DECISION SUPPORT block.</li>
          <li>Compliance guard ensures disclaimers and forbids guaranteed-outcome language.</li>
        </ul>
      </section>

      <section id="rc-livedata" className="rc-section">
        <h3>3. Live Data Status</h3>
        <div className="rc-grid">
          <div className="rc-card">
            <div className="rc-card__label">OANDA v20</div>
            <div className={`rc-card__value ${oandaConnected ? "is-on" : "is-warn"}`}>
              {oandaConnected ? "CONNECTED" : "KEYS REQUIRED"}
            </div>
            <div className="rc-card__sub">{liveStatus?.oanda?.url || "—"}</div>
          </div>
          <div className="rc-card">
            <div className="rc-card__label">TwelveData (Stocks/Crypto)</div>
            <div className={`rc-card__value ${liveStatus?.twelvedata?.configured ? "is-on" : "is-warn"}`}>
              {liveStatus?.twelvedata?.configured ? "CONNECTED" : "KEY REQUIRED"}
            </div>
            <div className="rc-card__sub">TWELVEDATA_API_KEY</div>
          </div>
          <div className="rc-card">
            <div className="rc-card__label">Alpha Vantage (Fallback)</div>
            <div className={`rc-card__value ${liveStatus?.alphavantage?.configured ? "is-on" : "is-warn"}`}>
              {liveStatus?.alphavantage?.configured ? "CONNECTED" : "KEY REQUIRED"}
            </div>
            <div className="rc-card__sub">ALPHAVANTAGE_API_KEY</div>
          </div>
          <div className="rc-card">
            <div className="rc-card__label">Current Session Freshness</div>
            <div className="rc-card__value">
              {(metadata?.data_freshness || "public").toUpperCase()}
            </div>
            <div className="rc-card__sub">Per-response label, never spoofed</div>
          </div>
        </div>
      </section>

      <section id="rc-mongo" className="rc-section">
        <h3>4. MongoDB Persistence</h3>
        <div className="rc-grid">
          <div className="rc-card">
            <div className="rc-card__label">Mongo</div>
            <div className={`rc-card__value ${mongoOk ? "is-on" : "is-off"}`}>
              {mongoOk ? "CONNECTED" : "DISCONNECTED"}
            </div>
          </div>
          <div className="rc-card">
            <div className="rc-card__label">Active Session</div>
            <div className="rc-card__value">{currentSessionId ? currentSessionId.slice(0, 8) + "…" : "—"}</div>
          </div>
          <div className="rc-card">
            <div className="rc-card__label">Active Mode</div>
            <div className="rc-card__value">{(activeMode || "—").toUpperCase()}</div>
          </div>
        </div>
      </section>

      <section id="rc-oanda" className="rc-section">
        <h3>5. OANDA Integration</h3>
        <p>
          Secure server-side proxy to OANDA v20 REST API. Add <code>OANDA_API_KEY</code> and{" "}
          <code>OANDA_ACCOUNT_ID</code> to the backend environment to enable live bid/ask/spread
          on supported instruments: EUR/USD, GBP/USD, USD/JPY, AUD/USD, USD/CAD, USD/CHF, XAU/USD,
          XTI/USD, EUR/GBP, CAD/JPY.
        </p>
      </section>

      <section id="rc-compliance" className="rc-section">
        <h3>6. Risk & Compliance Layer</h3>
        <ul>
          <li>Disclaimers auto-attached when missing.</li>
          <li>Forbidden-language detection (no guarantees, no risk-free claims, no execution claims).</li>
          <li>HITL flag raised when confidence is very high or risk is HIGH/EXTREME.</li>
        </ul>
      </section>

      <section id="rc-export" className="rc-section">
        <h3>7. Export Center</h3>
        <p>
          Generate a branded “CareLogix™ ORACLE Intelligence Dossier” PDF for the current
          session and mode. Each export is logged with an audit ID.
        </p>
      </section>

      <section id="rc-audit" className="rc-section">
        <h3>8. Audit Readiness</h3>
        <p>
          Audit log captures every chat response with verdict, confidence, risk level, freshness, and
          compliance flags. Sessions, messages, exports, and high-risk events are all persisted.
        </p>
        {watchlist?.length > 0 && (
          <div className="rc-watchlist">
            <div className="rc-card__label">Current Watchlist ({watchlist.length})</div>
            <ul>
              {watchlist.slice(0, 8).map((w) => (
                <li key={w.id || w.symbol}>
                  <span className="rc-pill">{w.mode?.toUpperCase()}</span> <b>{w.symbol}</b>
                  {w.note && <em> — {w.note}</em>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section id="rc-knowledge" className="rc-section">
        <h3>9. Knowledge Graph</h3>
        <p>
          Cross-session intelligence aggregated across every ORACLE answer. Drill into any asset to
          inspect the verdict timeline, average confidence, modes seen, and last activity.
        </p>
        <KnowledgeGraphPanel />
      </section>

      <section id="rc-alerts" className="rc-section">
        <h3>10. Watchlist Alerts</h3>
        <p>
          When the ORACLE&apos;s verdict on an asset on your watchlist changes (e.g., BUY → HOLD), an
          alert is generated automatically. Click the bell in the header to view and acknowledge.
        </p>
      </section>

      <section id="rc-auth" className="rc-section">
        <h3>11. Auth & RBAC</h3>
        <p>
          Roles: <b>analyst</b> (default), <b>reviewer</b>, <b>admin</b>. The first registered user is
          promoted to <b>admin</b>. When no token is present, the platform runs in default workspace
          mode with admin privileges for development.
        </p>
      </section>

      <section id="rc-roadmap" className="rc-section">
        <h3>12. Roadmap</h3>
        <ul>
          <li>Auth/RBAC granular permissions across every collection.</li>
          <li>Webhooks/Email/SMS for high-severity alerts.</li>
          <li>Multi-tenant org workspaces.</li>
        </ul>
      </section>

      <section id="rc-legal" className="rc-section">
        <h3>13. Legal Disclaimers</h3>
        <p>
          Research and education only. Not financial, investment, legal, or tax advice. CareLogix™ ORACLE
          does not execute trades or bets. Sports wagering is restricted to legal jurisdictions and 21+.
          Forex involves substantial risk of loss. Crypto assets can lose value rapidly. Options are high-risk
          derivatives. Past performance is not indicative of future results.
        </p>
      </section>
    </div>
  );
}
